import readXlsxFile from "read-excel-file/browser";

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

function replaceUnsafeControls(value: string): string {
  return Array.from(value, (character) => {
    const code = character.codePointAt(0) ?? 0;
    const unsafe =
      code <= 8 ||
      code === 11 ||
      code === 12 ||
      (code >= 14 && code <= 31) ||
      (code >= 127 && code <= 159);
    return unsafe ? " " : character;
  }).join("");
}

/**
 * Normalize characters that commonly arrive from Excel, Outlook, and copied
 * directories without stripping legitimate names or diacritics. The server
 * applies the same policy authoritatively before persistence.
 */
export function sanitizeImportCell(value: string): string {
  return replaceUnsafeControls(value)
    .normalize("NFKC")
    .replace(/[\u00a0\u2007\u202f]/g, " ")
    .replace(/[\u2018\u2019\u201a\u201b]/g, "'")
    .replace(/[\u201c\u201d\u201e\u201f]/g, '"')
    .replace(/[\u2010-\u2015\u2212]/g, "-")
    .replace(/[\u200b-\u200d\u2060\ufeff]/g, "")
    .replace(/[ \t]+/g, " ")
    .trim();
}

export type SpreadsheetCell = string | number | boolean | Date | null;

function cellText(value: SpreadsheetCell): string {
  if (value == null) return "";
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return sanitizeImportCell(String(value));
}

export function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function spreadsheetRowsToCsv(rows: SpreadsheetCell[][]): string {
  return rows
    .filter((row) => row.some((cell) => cellText(cell).length > 0))
    .map((row) => row.map((cell) => csvCell(cellText(cell))).join(","))
    .join("\n");
}

export async function readContactFile(
  file: File
): Promise<{ text: string; fileName: string; convertedFromExcel: boolean }> {
  const lower = file.name.toLowerCase();
  if (lower.endsWith(".xls")) {
    throw new Error(
      "Legacy .xls files are not supported. Save the file as .xlsx or .csv and try again."
    );
  }
  if (lower.endsWith(".xlsx") || file.type === XLSX_MIME) {
    const sheets = await readXlsxFile(file);
    const first = sheets[0];
    if (!first || first.data.length === 0) throw new Error("The Excel workbook has no rows.");
    return {
      text: spreadsheetRowsToCsv(first.data as SpreadsheetCell[][]),
      fileName: file.name.replace(/\.xlsx$/i, ".csv"),
      convertedFromExcel: true,
    };
  }
  if (lower.endsWith(".csv") || file.type === "text/csv" || file.type === "") {
    return {
      text: (await file.text()).replace(/^\ufeff/, ""),
      fileName: file.name,
      convertedFromExcel: false,
    };
  }
  throw new Error("Choose a .csv or .xlsx contact file.");
}
